import React, { useState, useEffect } from 'react';
import { Shield, Brain, AlertTriangle, Activity, Eye, Settings, Database, ArrowRight } from 'lucide-react';

const OutbreakAlertSystem = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const [particles, setParticles] = useState([]);
  const [scrollY, setScrollY] = useState(0);
  const [alerts, setAlerts] = useState([]);
  const [error, setError] = useState(null);
  const [visibleAlertCount, setVisibleAlertCount] = useState(2);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const alertsPerPage = 2;

  // API Configuration
  // Note: For this application to fetch real alerts, you need a backend server
  // running at the specified API_URL that serves alert data.
  // Example: a simple Node.js Express server with a /alerts endpoint.
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

  useEffect(() => {
    setIsVisible(true);

    // Generate floating particles for background animation
    const newParticles = Array.from({ length: 60 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 5 + 2,
      speed: Math.random() * 3 + 1,
      opacity: Math.random() * 0.6 + 0.2,
      color: Math.random() > 0.5 ? 'blue' : 'purple',
    }));
    setParticles(newParticles);

    // Add scroll event listener for parallax effect
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch alerts from API endpoint
  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const response = await fetch(`${API_URL}/alerts`);
        if (!response.ok) {
          // Throw an error if the HTTP response status is not OK (e.g., 404, 500)
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setAlerts(data);
      } catch (err) {
        // Catch and display any errors during the fetch operation
        setError(err.message || 'Failed to fetch alerts. Please ensure the backend is running and accessible.');
        console.error('Error fetching alerts:', err);
      }
    };

    fetchAlerts();
  }, [API_URL]); // Re-run effect if API_URL changes

  // Update mouse position for interactive gradient orb effect
  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePosition({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
  };

  // Open the alert details modal
  const openAlertModal = (alert) => {
    setSelectedAlert(alert);
  };

  // Close the alert details modal
  const closeAlertModal = () => {
    setSelectedAlert(null);
  };

  // Load more alerts when "Load More" button is clicked
  const handleLoadMore = () => {
    setVisibleAlertCount(prevCount => prevCount + alertsPerPage);
  };

  // Hide extra alerts when "View Less" button is clicked
  const handleViewLess = () => {
    setVisibleAlertCount(alertsPerPage);
  };

  // Determine text color based on alert severity
  const getSeverityColor = (severity) => {
    switch (severity.toLowerCase()) {
      case 'critical': return 'text-red-500';
      case 'high': return 'text-orange-500';
      case 'medium': return 'text-yellow-500';
      case 'low': return 'text-green-500';
      default: return 'text-gray-400';
    }
  };

  // Determine emoji icon based on alert severity
  const getSeverityIcon = (severity) => {
    switch (severity.toLowerCase()) {
      case 'critical': return '🔴';
      case 'high': return '🟠';
      case 'medium': return '�';
      case 'low': return '🟢';
      default: return '⚪';
    }
  };

  // Determine background gradient and border color for alert cards based on severity
  const getSeverityBgColor = (severity) => {
    switch (severity.toLowerCase()) {
      case 'critical': return 'bg-gradient-to-br from-red-50 to-pink-50 border-red-200 hover:from-red-100 hover:to-pink-100 hover:shadow-red-500/30';
      case 'high': return 'bg-gradient-to-br from-orange-50 to-red-50 border-orange-200 hover:from-orange-100 hover:to-red-100 hover:shadow-orange-500/30';
      case 'medium': return 'bg-gradient-to-br from-yellow-50 to-amber-50 border-yellow-200 hover:from-yellow-100 hover:to-amber-100 hover:shadow-yellow-500/30';
      case 'low': return 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200 hover:from-green-100 hover:to-emerald-100 hover:shadow-green-500/30';
      default: return 'bg-gradient-to-br from-gray-50 to-slate-50 border-gray-200 hover:from-gray-100 hover:to-slate-100 hover:shadow-gray-500/30';
    }
  };

  // Static data for severity levels display
  const severityLevels = [
    {
      level: 'Low',
      color: 'green',
      icon: '🟢',
      description: 'Minimal threat detected.',
      details: 'No immediate action required. Systems are stable and secure.'
    },
    {
      level: 'Medium',
      color: 'yellow',
      icon: '🟡',
      description: 'Moderate risk activity identified.',
      details: 'Review recommended. Potential for escalation if ignored.'
    },
    {
      level: 'High',
      color: 'orange',
      icon: '🟠',
      description: 'Significant threat detected.',
      details: 'Immediate attention required. Activate security protocols.'
    },
    {
      level: 'Critical',
      color: 'red',
      icon: '🔴',
      description: 'Severe outbreak or breach in progress.',
      details: 'Emergency response needed. Full lockdown and threat isolation initiated.'
    }
  ];

  // Slice alerts to display based on visibleAlertCount
  const displayedAlerts = alerts.slice(0, visibleAlertCount);
  // Check if all alerts have been loaded
  const allAlertsLoaded = visibleAlertCount >= alerts.length;

  return (
    <>
      <div
        className="relative min-h-screen bg-white overflow-hidden"
        onMouseMove={handleMouseMove}
      >
        {/* Dynamic Background Mesh */}
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `
                radial-gradient(circle at 20% 20%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 80% 80%, rgba(168, 85, 247, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 40% 60%, rgba(14, 165, 233, 0.08) 0%, transparent 50%)
              `,
              animation: 'meshMove 15s ease-in-out infinite',
            }}
          />
        </div>

        {/* Animated Grid */}
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `
                linear-gradient(to right, #3b82f6 1px, transparent 1px),
                linear-gradient(to bottom, #3b82f6 1px, transparent 1px)
              `,
              backgroundSize: '60px 60px',
              animation: 'gridPulse 8s ease-in-out infinite',
              transform: `translateY(${scrollY * 0.1}px)`,
            }}
          />
        </div>

        {/* Enhanced Floating Particles */}
        {particles.map((particle) => (
          <div
            key={particle.id}
            className={`absolute rounded-full ${
              particle.color === 'blue' ? 'bg-blue-400' : 'bg-purple-400'
            } shadow-lg`}
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              opacity: particle.opacity * 0.3,
              animation: `particleFloat ${particle.speed * 4 + 6}s ease-in-out infinite`,
              animationDelay: `${particle.id * 0.05}s`,
              pointerEvents: 'none',
              boxShadow: `0 0 ${particle.size * 2}px ${
                particle.color === 'blue' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(168, 85, 247, 0.3)'
              }`,
            }}
          />
        ))}

        {/* Interactive Gradient Orb */}
        <div
          className="absolute inset-0 pointer-events-none transition-all duration-300"
          style={{
            background: `radial-gradient(800px circle at ${mousePosition.x}% ${mousePosition.y}%, rgba(59, 130, 246, 0.08), rgba(168, 85, 247, 0.05) 40%, transparent 70%)`,
          }}
        />

        {/* Animated Tech Elements */}
        <div
          className="absolute top-32 left-10 w-20 h-20 border-2 border-blue-300 rounded-2xl transform rotate-45 opacity-20"
          style={{
            animation: 'techSpin 25s linear infinite',
          }}
        />
        <div
          className="absolute bottom-32 right-10 w-16 h-16 border-2 border-purple-300 rounded-full opacity-30"
          style={{
            animation: 'techPulse 4s ease-in-out infinite'
          }}
        />
        <div
          className="absolute top-1/3 right-20 w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full opacity-20"
          style={{
            animation: 'techBounce 6s ease-in-out infinite',
            animationDelay: '1s'
          }}
        />

        {/* Main Content */}
        <div className="relative z-10 px-6 pt-8 pb-12">
          <div className="max-w-7xl mx-auto">
            {/* Header Section */}
            <div className="text-center mb-8">
              <div
                className={`inline-flex items-center gap-3 px-6 py-3 rounded-full border border-blue-300 bg-gradient-to-r from-blue-100 to-purple-100 mb-4 transform transition-all duration-1200 ${
                  isVisible ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-12 opacity-0 scale-95'
                }`}
              >
                <div className="w-3 h-3 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 animate-pulse shadow-lg" />
                <span className="text-sm font-semibold text-blue-700 tracking-wide uppercase">
                  🚦 Real-Time Threat Monitoring
                </span>
              </div>

              <h1
                className={`text-4xl md:text-6xl lg:text-7xl font-black leading-none tracking-tight mb-4 transform transition-all duration-1200 delay-300 ${
                  isVisible ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-12 opacity-0 scale-95'
                }`}
              >
                <span className="block bg-gradient-to-r from-blue-600 via-purple-600 to-cyan-600 bg-clip-text text-transparent">
                  Outbreak Alert System
                </span>
                <span className="block text-gray-900 text-3xl md:text-4xl lg:text-5xl mt-1">
                  GQBS LAB
                </span>
              </h1>
            </div>

            {/* Main Description */}
            <div
              className={`max-w-5xl mx-auto mb-10 transform transition-all duration-1200 delay-500 ${
                isVisible ? 'translate-y-0 opacity-100 rotate-0' : 'translate-y-12 opacity-0 rotate-1'
              }`}
            >
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-3xl p-6 md:p-8 border border-blue-200 shadow-lg">
                <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-4">
                  Stay ahead with our real-time{' '}
                  <strong className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    Outbreak Alert System
                  </strong>
                  , powered by{' '}
                  <strong className="text-blue-600">BIQUA</strong> and{' '}
                  <strong className="text-purple-600">QUBISA</strong>.
                </p>
                <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-6">
                  Get instant threat detection and security-level insights across all integrated systems.
                </p>

                <div className="text-center bg-white rounded-2xl p-4 border border-blue-100 shadow-sm mb-4">
                  <p className="text-lg font-bold text-gray-800 mb-2">
                    <span className="text-blue-600">Real-time Monitoring</span> |{' '}
                    <span className="text-purple-600">Auto-Response Ready</span> |{' '}
                    <span className="text-cyan-600">Adaptive Intelligence</span>
                  </p>
                  <p className="text-sm text-gray-600">
                    This system ensures <strong>zero-delay alerts</strong>, helping your team{' '}
                    <strong>respond faster and smarter</strong> — because in quantum-secured environments, every second matters.
                  </p>
                </div>
              </div>
            </div>

            {/* Severity Levels Section */}
            <div
              className={`mb-10 transform transition-all duration-1200 delay-700 ${
                isVisible ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-12 opacity-0 scale-95'
              }`}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-8 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                🚦 Alert Severity Levels
              </h2>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {severityLevels.map((level, index) => (
                  <div
                    key={level.level}
                    className={`group p-6 rounded-3xl border hover:scale-110 transition-all duration-700 cursor-pointer shadow-lg hover:shadow-2xl transform hover:rotate-1 ${
                      level.color === 'green' ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-300 hover:from-green-100 hover:to-emerald-100 hover:shadow-green-500/50' :
                      level.color === 'yellow' ? 'bg-gradient-to-br from-yellow-50 to-amber-50 border-yellow-300 hover:from-yellow-100 hover:to-amber-100 hover:shadow-yellow-500/50' :
                      level.color === 'orange' ? 'bg-gradient-to-br from-orange-50 to-red-50 border-orange-300 hover:from-orange-100 hover:to-red-100 hover:shadow-orange-500/50' :
                      'bg-gradient-to-br from-red-50 to-pink-50 border-red-300 hover:from-red-100 hover:to-pink-100 hover:shadow-red-500/50'
                    }`}
                    style={{ animationDelay: `${900 + index * 250}ms` }}
                  >
                    <div className="text-3xl mb-4 group-hover:scale-125 group-hover:rotate-12 transition-all duration-500">
                      {level.icon}
                    </div>

                    <h3 className={`text-xl font-black mb-3 group-hover:scale-105 transition-all duration-300 ${
                      level.color === 'green' ? 'bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent' :
                      level.color === 'yellow' ? 'bg-gradient-to-r from-yellow-600 to-amber-600 bg-clip-text text-transparent' :
                      level.color === 'orange' ? 'bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent' :
                      'bg-gradient-to-r from-red-600 to-pink-600 bg-clip-text text-transparent'
                    }`}>
                      {level.level}
                    </h3>

                    <p className="text-gray-700 mb-2 font-semibold text-sm group-hover:text-gray-800 transition-colors">
                      {level.description}
                    </p>

                    <p className="text-gray-600 text-xs leading-relaxed group-hover:text-gray-700 transition-colors">
                      {level.details}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-center">
                Error: {error}
              </div>
            )}

            {/* Active Alerts Section - Enhanced Card Design */}
            {alerts.length > 0 && (
              <div
                className={`mb-10 transform transition-all duration-1000 delay-800 ${
                  isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                }`}
              >
                <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-8 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  🚨 Active Outbreak Alerts
                </h2>

                <div className="grid md:grid-cols-2 gap-6 max-w-7xl mx-auto">
                  {displayedAlerts.map((alert, index) => (
                    <div
                      key={alert._id}
                      className={`group p-6 rounded-3xl border transition-all duration-500 cursor-pointer shadow-lg hover:shadow-2xl transform hover:scale-105 hover:-rotate-1 ${getSeverityBgColor(alert.severity)}`}
                      style={{ animationDelay: `${1000 + index * 300}ms` }}
                    >
                      {/* Alert Header */}
                      <div className="flex justify-between items-center mb-4 border-b border-dashed border-gray-300 border-opacity-50 pb-3">
                        <span className="text-sm text-gray-500 font-mono">
                          <strong className="text-gray-700">{new Date(alert.date).toLocaleDateString()}</strong>
                        </span>
                        <span className={`text-sm font-bold ${getSeverityColor(alert.severity)} flex items-center gap-1`}>
                          <span className="text-lg">{getSeverityIcon(alert.severity)}</span>
                          SEVERITY: {alert.severity.toUpperCase()}
                        </span>
                      </div>

                      {/* Alert Title */}
                      <div className="mb-3">
                        <h3 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent tracking-wide group-hover:scale-105 transition-transform duration-300">
                          {alert.name}
                        </h3>
                      </div>

                      {/* Alert Type */}
                      <div className="mb-4 text-sm text-gray-600">
                        <p><strong>Type:</strong> <span className="text-blue-600 font-semibold">{alert.type}</span></p>
                      </div>

                      {/* Alert Body */}
                      <div className="flex flex-col md:flex-row gap-4 flex-grow mb-4">
                        {alert.iconUrl && (
                          <img 
                            src={alert.iconUrl} 
                            alt={alert.name} 
                            className="w-20 h-20 rounded-lg object-cover border border-blue-200 shadow-md flex-shrink-0 group-hover:scale-110 transition-transform duration-300" 
                          />
                        )}
                        
                        <div className="flex-grow">
                          {/* Show first question for preview */}
                          {alert.questions && alert.questions.length > 0 && (
                            <div className="mb-3">
                              <p className="text-gray-800 font-bold text-sm mb-1">{alert.questions[0].question}</p>
                              <p className="text-gray-600 text-sm leading-relaxed">
                                {alert.questions[0].answer.length > 120 
                                  ? `${alert.questions[0].answer.substring(0, 120)}...` 
                                  : alert.questions[0].answer
                                }
                              </p>
                            </div>
                          )}
                          
                          {/* Read More button */}
                          {alert.questions && alert.questions.length > 1 && (
                            <button
                              onClick={() => openAlertModal(alert)}
                              className="text-blue-600 text-sm cursor-pointer hover:text-purple-600 hover:underline transition-colors duration-300 inline-flex items-center gap-1"
                            >
                              Read More <ArrowRight className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Alert Links */}
                      <div className="flex justify-between items-center pt-4 border-t border-dashed border-gray-300 border-opacity-50">
                        <a 
                          href={`/alerts/${alert._id}`} 
                          target='_blank' 
                          rel="noopener noreferrer" 
                          className='inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold text-sm rounded-full shadow-lg hover:from-purple-600 hover:to-blue-600 transition-all duration-300 hover:scale-110 hover:shadow-blue-500/50'
                        >
                          <Database className="w-4 h-4" />
                          Outbreak Details
                        </a>
                        
                        <button
                          onClick={() => openAlertModal(alert)}
                          className="inline-flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors duration-300 text-sm font-semibold"
                        >
                          <Eye className="w-4 h-4" />
                          View All Details
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Load More / View Less buttons */}
                <div className="text-center mt-10 space-y-4">
                  {!allAlertsLoaded && (
                    <button
                      onClick={handleLoadMore}
                      className="group px-8 py-3 bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 text-white rounded-full font-bold text-base hover:scale-115 transform transition-all duration-500 shadow-xl hover:shadow-orange-500/50 hover:rotate-1 uppercase tracking-wide"
                    >
                      <span className="flex items-center gap-2">
                        Load More Outbreak Alerts
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-2 group-hover:scale-110 transition-all duration-300" />
                      </span>
                    </button>
                  )}

                  {visibleAlertCount > alertsPerPage && (
                    <button
                      onClick={handleViewLess}
                      className="group px-6 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-full font-bold text-sm hover:scale-110 transform transition-all duration-500 shadow-lg hover:shadow-blue-500/50 ml-4 hover:-rotate-1 uppercase tracking-wide"
                    >
                      <span className="flex items-center gap-1">
                        View Less Outbreak Alerts
                        <ArrowRight className="w-3 h-3 rotate-180 group-hover:-translate-x-1 transition-transform duration-300" />
                      </span>
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Call to Action */}
            <div
              className={`text-center transform transition-all duration-1200 delay-1200 ${
                isVisible ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-12 opacity-0 scale-95'
              }`}
            >
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-3xl p-6 border border-blue-200 shadow-lg">
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
                  Ready to Secure Your Systems?
                </h3>
                <p className="text-base text-gray-600 mb-6 max-w-2xl mx-auto">
                  Join the next generation of cybersecurity with GQBS LAB's quantum-powered threat detection and response system.
                </p>
                <button className="group px-8 py-3 bg-gradient-to-r from-blue-600 via-purple-600 to-cyan-600 text-white rounded-full font-bold text-base hover:scale-115 transform transition-all duration-500 shadow-xl hover:shadow-blue-500/50 hover:rotate-1">
                  <span className="flex items-center gap-2">
                    Activate Security Protocols
                    <Shield className="w-4 h-4 group-hover:scale-125 group-hover:rotate-12 transition-all duration-300" />
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Alert Modal */}
        {selectedAlert && (
          <div
            className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[9999] p-4 backdrop-blur-sm" // Increased z-index to ensure it's on top
            onClick={closeAlertModal}
          >
            <div className="bg-white rounded-3xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto border-2 border-blue-200 shadow-2xl transform transition-all duration-300 scale-100" onClick={(e) => e.stopPropagation()}>
              {/* Modal Header */}
              <div className="flex justify-between items-start mb-6 pb-4 border-b border-gray-200">
                <div className="flex-1">
                  <h3 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-3 font-mono">
                    {selectedAlert.name}
                  </h3>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 font-mono">
                    <span>Released: <strong className="text-blue-600">{new Date(selectedAlert.date).toLocaleDateString()}</strong></span>
                    <span>Type: <strong className="text-blue-600">{selectedAlert.type}</strong></span>
                    <span className={`font-bold flex items-center gap-1 ${getSeverityColor(selectedAlert.severity)}`}>
                      <span className="text-lg">{getSeverityIcon(selectedAlert.severity)}</span>
                      {selectedAlert.severity.toUpperCase()}
                    </span>
                  </div>
                </div>
                <button
                  onClick={closeAlertModal}
                  className="w-10 h-10 bg-red-100 hover:bg-red-200 rounded-full flex items-center justify-center text-red-600 font-bold text-xl transition-all duration-300 hover:scale-110 hover:rotate-90 ml-4"
                >
                  ×
                </button>
              </div>

              {/* Modal Questions */}
              <div className="space-y-4 mb-6">
                {selectedAlert.questions && selectedAlert.questions.map((q, i) => (
                  <div key={i} className="p-4 rounded-2xl border border-blue-200 bg-gradient-to-r from-blue-50 to-purple-50 shadow-sm hover:shadow-md transition-shadow duration-300">
                    <p className="font-bold text-blue-600 mb-2 text-lg font-mono">{q.question}</p>
                    <p className="text-gray-700 leading-relaxed font-mono text-base">{q.answer}</p>
                  </div>
                ))}
              </div>

              {/* Modal Footer */}
              <div className="text-center pt-4 border-t border-gray-200">
                <a
                  href={`/alerts/${selectedAlert._id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full font-bold text-lg hover:scale-110 transform transition-all duration-500 shadow-xl hover:shadow-blue-500/50 hover:rotate-1"
                >
                  <Database className="w-5 h-5" />
                  View Full Outbreak Details
                  <ArrowRight className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Custom Styles */}
        <style jsx>{`
          @keyframes meshMove {
            0%, 100% {
              transform: translate(0, 0) rotate(0deg);
            }
            33% {
              transform: translate(30px, -20px) rotate(120deg);
            }
            66% {
              transform: translate(-20px, 30px) rotate(240deg);
            }
          }

          @keyframes gridPulse {
            0%, 100% {
              opacity: 0.05;
              transform: scale(1);
            }
            50% {
              opacity: 0.1;
              transform: scale(1.02);
            }
          }

          @keyframes particleFloat {
            0%, 100% {
              transform: translateY(0px) rotate(0deg) scale(1);
            }
            25% {
              transform: translateY(-20px) rotate(90deg) scale(1.1);
            }
            50% {
              transform: translateY(-10px) rotate(180deg) scale(0.9);
            }
            75% {
              transform: translateY(-15px) rotate(270deg) scale(1.05);
            }
          }

          @keyframes techSpin {
            0% {
              transform: rotate(45deg) scale(1);
            }
            50% {
              transform: rotate(225deg) scale(1.1);
            }
            100% {
              transform: rotate(405deg) scale(1);
            }
          }

          @keyframes techPulse {
            0%, 100% {
              transform: scale(1);
              opacity: 0.3;
            }
            50% {
              transform: scale(1.2);
              opacity: 0.6;
            }
          }

          @keyframes techBounce {
            0%, 100% {
              transform: translateY(0px) scale(1);
            }
            25% {
              transform: translateY(-20px) scale(1.1);
            }
            50% {
              transform: translateY(-10px) scale(0.9);
            }
            75% {
              transform: translateY(-15px) scale(1.05);
            }
          }

          /* Custom scrollbar for modal */
          .overflow-y-auto::-webkit-scrollbar {
            width: 8px;
          }
          .overflow-y-auto::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 10px;
          }
          .overflow-y-auto::-webkit-scrollbar-thumb {
            background: linear-gradient(to bottom, #3b82f6, #8b5cf6);
            border-radius: 10px;
            border: 2px solid #f1f5f9;
          }
          .overflow-y-auto::-webkit-scrollbar-thumb:hover {
            background: linear-gradient(to bottom, #2563eb, #7c3aed);
          }

          /* Enhanced hover effects */
          .group:hover .group-hover\\:scale-125 {
            transform: scale(1.25);
          }
          .group:hover .group-hover\\:rotate-12 {
            transform: rotate(12deg);
          }
          .group:hover .group-hover\\:scale-110 {
            transform: scale(1.1);
          }
          .group:hover .group-hover\\:translate-x-2 {
            transform: translateX(8px);
          }
          .group:hover .group-hover\\:-translate-x-1 {
            transform: translateX(-4px);
          }

          /* Typing effect animation (not directly used in this version but kept for reference) */
          @keyframes typing {
            from { width: 0 }
            to { width: 100% }
          }

          @keyframes blink-caret {
            from, to { border-color: transparent }
            50% { border-color: #3b82f6; }
          }

          .typing-effect {
            overflow: hidden;
            border-right: .15em solid #3b82f6;
            white-space: nowrap;
            margin: 0 auto;
            letter-spacing: .05em;
            animation:
              typing 3s steps(40, end),
              blink-caret .75s step-end infinite;
          }

          /* Glow effects for severity indicators (not directly used in this version but kept for reference) */
          .severity-glow-critical {
            box-shadow: 0 0 20px rgba(239, 68, 68, 0.3);
          }
          .severity-glow-high {
            box-shadow: 0 0 20px rgba(249, 115, 22, 0.3);
          }
          .severity-glow-medium {
            box-shadow: 0 0 20px rgba(245, 158, 11, 0.3);
          }
          .severity-glow-low {
            box-shadow: 0 0 20px rgba(34, 197, 94, 0.3);
          }

          /* Enhanced card animations (not directly used in this version but kept for reference) */
          .alert-card-entrance {
            animation: cardSlideUp 0.6s ease-out forwards;
          }

          @keyframes cardSlideUp {
            from {
              transform: translateY(50px) scale(0.9);
            }
            to {
              transform: translateY(0) scale(1);
            }
          }
        `}</style>
      </div>
    </>
  );
};

export default OutbreakAlertSystem;
